import { type TFunction } from "i18next";

import { cn } from "@/lib/utils";

import { RelativeDateCell } from "@/components/RelativeDateCell";
import { formatDateTimeCompact, formatRelativeDateTime } from "@/utils/relative-time";

import { ActionsCell } from "./ActionsCell";
import { MetricValue } from "./MetricValue";
import { NameCell } from "./NameCell";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { TextValue } from "./TextValue";
import { COMPACT_HEADER_CLASS, NUMBER_COLUMN_WIDTH_CLASS, NUMERIC_HEADER_CLASS } from "./constants";
import { type AggregatorColumnDef, type AggregatorListItem, type MetricTone } from "./types";
import {
  getAggregatorArea,
  getAggregatorName,
  getLastActiveSourceLabel,
  getLastActiveTimestamp,
} from "./utils";

const createNumberColumn = (
  id: string,
  label: string,
  accessor: (row: AggregatorListItem) => number | null | undefined,
  options: { highlightLow?: boolean; tone?: MetricTone } = {}
): AggregatorColumnDef => ({
  id,
  accessorFn: accessor,
  header: ({ column }) => <SortableColumnHeader align="center" column={column} label={label} />,
  cell: ({ row }) => <MetricValue {...options} value={accessor(row.original)} />,
  sortDescFirst: false,
  sortingFn: "basic",
  sortUndefined: "last",
  meta: {
    cellClassName: cn(NUMBER_COLUMN_WIDTH_CLASS, "px-3 text-center"),
    headerClassName: cn(NUMBER_COLUMN_WIDTH_CLASS, NUMERIC_HEADER_CLASS),
  },
});

const createColumns = (
  t: TFunction,
  onClickView?: (aggregator: AggregatorListItem) => void
): AggregatorColumnDef[] => {
  return [
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => <ActionsCell aggregator={row.original} onClickView={onClickView} t={t} />,
      enableSorting: false,
      meta: {
        cellClassName: "w-0 px-3",
        headerClassName: "w-0",
      },
    },
    {
      id: "name",
      accessorFn: getAggregatorName,
      header: ({ column }) => (
        <SortableColumnHeader column={column} label={t("aggregators.list.columns.name")} />
      ),
      cell: ({ row }) => <NameCell aggregator={row.original} />,
      sortDescFirst: false,
      sortingFn: "alphanumeric",
      sortUndefined: "last",
      meta: {
        cellClassName: "min-w-48 max-w-64 whitespace-normal px-3",
        headerClassName: cn("min-w-48 max-w-64", COMPACT_HEADER_CLASS),
      },
    },
    {
      id: "area",
      accessorFn: getAggregatorArea,
      header: ({ column }) => (
        <SortableColumnHeader column={column} label={t("aggregators.list.columns.area")} />
      ),
      cell: ({ row }) => (
        <TextValue
          className="block whitespace-normal wrap-break-words leading-tight"
          value={getAggregatorArea(row.original)}
        />
      ),
      sortDescFirst: false,
      sortingFn: "alphanumeric",
      sortUndefined: "last",
      meta: {
        cellClassName: "min-w-32 max-w-56 whitespace-normal px-3",
        headerClassName: cn("min-w-32 max-w-56", COMPACT_HEADER_CLASS),
      },
    },
    {
      id: "lastActive",
      accessorFn: getLastActiveTimestamp,
      header: ({ column }) => (
        <SortableColumnHeader column={column} label={t("aggregators.list.columns.lastActive")} />
      ),
      cell: ({ row }) => (
        <RelativeDateCell
          relative={formatRelativeDateTime(row.original.last_active, t)}
          absolute={formatDateTimeCompact(row.original.last_active)}
          caption={getLastActiveSourceLabel(row.original.last_active_source, t)}
        />
      ),
      sortDescFirst: false,
      sortingFn: "basic",
      sortUndefined: "last",
      meta: {
        cellClassName: "min-w-36 px-3",
        headerClassName: cn("min-w-36", COMPACT_HEADER_CLASS),
      },
    },
    createNumberColumn("farmers", t("aggregators.list.columns.farmers"), row => row.farmers, {
      tone: "primary",
    }),
    createNumberColumn(
      "farmersTarget",
      t("aggregators.list.columns.farmersTarget"),
      row => row.farmers_target
    ),
    createNumberColumn(
      "eventsCreated",
      t("aggregators.list.columns.eventsCreated"),
      row => row.events_created
    ),
    createNumberColumn(
      "harvestsCreated",
      t("aggregators.list.columns.harvestsCreated"),
      row => row.harvests_created
    ),
    createNumberColumn("deposits", t("aggregators.list.columns.deposits"), row => row.deposits, {
      highlightLow: true,
    }),
    createNumberColumn(
      "offersReceived",
      t("aggregators.list.columns.offersReceived"),
      row => row.offers_received
    ),
    createNumberColumn(
      "offersAccepted",
      t("aggregators.list.columns.offersAccepted"),
      row => row.offers_accepted,
      { highlightLow: true }
    ),
    createNumberColumn("exchanges", t("aggregators.list.columns.exchanges"), row => row.exchanges, {
      highlightLow: true,
    }),
    createNumberColumn(
      "farmerPayments",
      t("aggregators.list.columns.farmerPayments"),
      row => row.farmer_payments
    ),
  ];
};

export { createColumns };
